import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const NavigationManager = () => {
  const navigate = useNavigate();
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchNavItems();
  }, []);

  const fetchNavItems = async () => {
    try {
      const response = await api.get('/admin/navigation');
      setNavItems(response.data || []);
      setHasChanges(false);
    } catch (err) {
      setError('Failed to load navigation items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      label: 'New Page',
      path: '/new-page',
      order_index: navItems.length,
      is_enabled: true,
      is_published: false,
      isNew: true
    };
    setNavItems([...navItems, newItem]);
    setHasChanges(true);
  };

  const handleUpdateItem = (id, field, value) => {
    setNavItems(items =>
      items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
    setHasChanges(true);
  };

  const handleRemoveItem = (id) => {
    setNavItems(items => items.filter(item => item.id !== id));
    setHasChanges(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newItems = [...navItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    // Update order_index
    newItems.forEach((item, idx) => {
      item.order_index = idx;
    });
    setNavItems(newItems);
    setHasChanges(true);
  };

  const handleMoveDown = (index) => {
    if (index === navItems.length - 1) return;
    const newItems = [...navItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    // Update order_index
    newItems.forEach((item, idx) => {
      item.order_index = idx;
    });
    setNavItems(newItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Remove temporary IDs and isNew flag
      const itemsToSave = navItems.map(({ id, isNew, ...item }) => ({
        ...item,
        is_published: false // Save as draft first
      }));

      await api.put('/admin/navigation', itemsToSave);
      setSuccess('Navigation saved successfully!');
      setHasChanges(false);
      fetchNavItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save navigation');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Publish all navigation changes? This will make them live on the website.')) return;

    try {
      await api.post('/admin/navigation/publish');
      setSuccess('Navigation published successfully!');
      fetchNavItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish navigation');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Discard all unsaved changes?')) return;
    fetchNavItems();
    setHasChanges(false);
  };

  if (loading) return <div className="loading">Loading navigation...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Navigation Manager</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        <div className="navigation-editor">
          <div className="editor-header">
            <h2>Navigation Items</h2>
            <div className="editor-actions">
              <button onClick={handleAddItem} className="btn btn-primary">
                + Add Item
              </button>
              {hasChanges && (
                <>
                  <button onClick={handleReset} className="btn btn-secondary">
                    Reset Changes
                  </button>
                  <button onClick={handleSave} className="btn btn-success">
                    Save Draft
                  </button>
                </>
              )}
              {!hasChanges && navItems.length > 0 && (
                <button onClick={handlePublish} className="btn btn-success">
                  Publish Navigation
                </button>
              )}
            </div>
          </div>

          {navItems.length === 0 ? (
            <div className="empty-state">
              <p>No navigation items. Click "Add Item" to create one.</p>
            </div>
          ) : (
            <div className="nav-items-list">
              {navItems.map((item, index) => (
                <div key={item.id} className="nav-item-editor">
                  <div className="nav-item-order">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="btn-icon"
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <span className="order-number">{index + 1}</span>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === navItems.length - 1}
                      className="btn-icon"
                      title="Move Down"
                    >
                      ↓
                    </button>
                  </div>

                  <div className="nav-item-fields">
                    <div className="form-group">
                      <label>Label</label>
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => handleUpdateItem(item.id, 'label', e.target.value)}
                        placeholder="e.g., Home, About, Products"
                      />
                    </div>

                    <div className="form-group">
                      <label>Path</label>
                      <input
                        type="text"
                        value={item.path}
                        onChange={(e) => handleUpdateItem(item.id, 'path', e.target.value)}
                        placeholder="e.g., /, /about, /products"
                      />
                    </div>

                    <div className="form-group-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={item.is_enabled}
                          onChange={(e) => handleUpdateItem(item.id, 'is_enabled', e.target.checked)}
                        />
                        <span>Enabled</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="btn-icon btn-delete"
                    title="Remove Item"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="editor-info">
            <h3>Common Navigation Paths</h3>
            <ul>
              <li><code>/</code> - Home page</li>
              <li><code>/about</code> - About page</li>
              <li><code>/products</code> - Products listing</li>
              <li><code>/cart</code> - Shopping cart</li>
              <li><code>/blog</code> - Blog page</li>
              <li><code>/contact</code> - Contact page</li>
            </ul>
            <p><strong>Note:</strong> Save your changes as a draft first, then publish when ready to make them live.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationManager;
